import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck, KeyRound, Mail, Sparkles } from "lucide-react";
import { AverageAfSignIn } from "@/components/member/AverageAfSignIn";
import { firebaseEnabled } from "@/lib/firebase";
import { api, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const MemberAuthPanel = () => {
  const { signIn, aaf } = useMember();
  const [mode, setMode] = useState(firebaseEnabled ? "account" : "link");
  useEffect(() => { if (aaf && firebaseEnabled) setMode("account"); }, [aaf]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "link") {
        await api.post("/member/request-link", { email });
        setSent(true);
      } else {
        const { data } = await api.post("/member/login", { email, password });
        signIn(data.token, data.member);
        toast.success(`Welcome back, ${data.member.name.split(" ")[0]}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="member-link-sent" className="rounded-2xl bg-[#E5EDE3] border border-[#8FA58A]/40 p-6 text-center">
        <MailCheck className="w-8 h-8 text-[#5A7355] mx-auto mb-2" />
        <p className="font-semibold text-[#1B2A41]">Check your inbox</p>
        <p className="text-sm text-[#5C6A79] mt-1">If <strong>{email}</strong> is registered as a F.E.R.M, a one-time sign-in link is on its way. It expires in 30 minutes.</p>
        <button type="button" onClick={() => setSent(false)} className="mt-3 text-xs text-[#5A7355] underline">Use a different email</button>
      </div>
    );
  }

  const tab = (v, label, Icon) => (
    <button type="button" onClick={() => setMode(v)} data-testid={`member-auth-tab-${v}`} className={`flex-1 h-10 px-2 rounded-full inline-flex items-center justify-center gap-1.5 text-[11px] font-type font-bold uppercase tracking-wider transition-colors ${mode === v ? "bg-[#1B2A41] text-[#FDFBF7]" : "text-[#1B2A41] hover:bg-[#1B2A41]/5"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div data-testid="member-auth-panel" className="rounded-2xl bg-white border border-[#1B2A41]/10 shadow-lg p-6 space-y-4">
      <div>
        <h2 className="font-type font-bold text-base md:text-lg uppercase tracking-wider text-[#1B2A41]">Sign in to My ROAN</h2>
        <p className="text-sm text-[#5C6A79] mt-1">{firebaseEnabled ? "Use your Average AF account, or the email you registered with." : "Use the email you registered with. No password? The email link works every time."}</p>
      </div>
      <div className="flex gap-1 p-1 rounded-full bg-[#FAF8F2] border border-[#D8D2C4]">
        {firebaseEnabled && tab("account", "Average AF", Sparkles)}
        {tab("link", "Email link", Mail)}
        {tab("password", "ROAN password", KeyRound)}
      </div>
      {mode === "account" && <AverageAfSignIn />}
      {mode !== "account" && <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">Email</Label>
        <Input data-testid="member-auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11" />
      </div>
      {mode === "password" && (
        <div className="space-y-1.5">
          <Label className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">Password</Label>
          <Input data-testid="member-auth-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
          <p className="text-[11px] text-[#5C6A79]">Set or reset your password any time from inside My ROAN after signing in with the email link.</p>
        </div>
      )}
      <button type="submit" disabled={busy} data-testid="member-auth-submit" className="w-full h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {mode === "link" ? "Send my sign-in link" : "Sign in"}
      </button>
      </form>}
    </div>
  );
};
